<script setup lang="ts">
import { api } from '@/api';
import {
  instanceOfAllCombinationsDisabledExceptionDetails,
  instanceOfDiscreteNumericValueDto,
  instanceOfEnumerationValueDto,
  instanceOfEnumerationValueListDto,
  instanceOfFaultyNumericValueDto,
  instanceOfMvTherapySystemSetupDto,
  instanceOfNumericListDto,
  instanceOfNumericValueDto,
  instanceOfTextValueDto,
  instanceOfTooManySetupCombinationsExceptionDetails,
  ISingleSystemSetupParameter,
  MedicalSystemRtSystemDto,
  MedicalSystemSetupDto,
  MvTherapySystemSetupDto,
  ParameterCombinationDto,
  ParameterCombinationTableDto,
  QaEquipmentRtSystemDto,
  TaskItemAreaDto,
  TaskItemQaEquipmentSetupDto,
  TaskItemReferenceMeasurementResultsDto,
  TaskTypeDescription,
} from '@/api/generator';
import TimeStamp from '@/components/EntityHistory/components/TimeStamp.vue';
import Checkbox from '@/components/forms/Checkbox/Checkbox.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import Loader from '@/components/Loader/Loader.vue';
import ReferenceMeasurementDialog from '@/components/measurement/ReferenceMeasurementDialog.vue';
import { Measurement } from '@/components/measurement/types';
import { useReferenceMeasurement } from '@/components/measurement/utils/useReferenceMeasurement';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types';
import ParameterTable from '@/components/ParameterTable/ParameterTable.vue';
import TaskStepTitle from '@/components/TaskStepTitle/TaskStepTitle.vue';
import { useTaskStepTitle } from '@/components/TaskStepTitle/useTaskStepTitles.ts';
import { truthy } from '@/utils/helper.ts';
import { useLoading } from '@/utils/useLoading';
import { useReferenceMeasurementInfo } from '@/utils/useReferenceMeasurementInfo.ts';
import {
  toCreateOrUpdateMedicalSystemSetupBaseDto,
  toCreateParameterCombinationTableDto,
  toCreateTaskItemQaEuipmentSetupDto,
} from '@/views/tasks/mapping.ts';
import { useCombinationsValidationState } from '@/views/tasks/utils.ts';
import { useTranslation } from 'i18next-vue';
import { cloneDeep, isArray, isEmpty, isEqual, isNil, isObject, keyBy, negate } from 'lodash';
import { NIL } from 'uuid';
import { useField, useFieldError, useValidateForm } from 'vee-validate';
import { computed, onMounted, ref, toRef, watch, watchEffect } from 'vue';
import { checkIfBeamQualitiesAreSelected } from './SetupCombinationsUtil.ts';
import Button from '@/components/buttons/Button.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { getActionNotPossibleReason } from '@/utils/actions';
import { I18nKey } from '@/utils/typings';
import ReferenceMeasurementResults from './ReferenceMeasurementResults.vue';
import { ignoreValidationErrorHandler } from '@/errorHandling/errorHandler.ts';
import SetupCombinationsUpdateNotification from './SetupCombinationsUpdateNotification.vue';
import pDebounce from 'p-debounce';
import { isSemanticError } from '@/errorHandling/index.ts';

const { t } = useTranslation();

const props = defineProps<{
  name: string;
  area: TaskItemAreaDto;
  medicalSystemSetup?: MvTherapySystemSetupDto | MedicalSystemSetupDto;
  medicalSystem?: MedicalSystemRtSystemDto;
  qaEquipmentSetups?: TaskItemQaEquipmentSetupDto[];
  qaEquipmentSystemEntities?: QaEquipmentRtSystemDto[];
  taskType: TaskTypeDescription;
}>();

const { setValue: setCominationSetIdValue } = useField(() => `${props.name}.combinationSetId`);

const combinationSetIdError = useFieldError(toRef(() => `${props.name}.combinationSetId`));

const hasCombinationSetIdError = computed(() => combinationSetIdError.value !== undefined);

const combinationTable = defineModel<ParameterCombinationTableDto | undefined>('combinationTable', {
  required: true,
});

const mode = useFormMode();

const validate = useValidateForm();

const { setAreaCombinationChanged, isCombinationSelectionChangedForArea } =
  useCombinationsValidationState();

const items = computed(() =>
  combinationTable.value?.combinations?.map((combination) => ({
    combination,
    parameters: keyBy(
      [...(combination.targetSystemValues || []), ...(combination.supportSystemValues || [])],
      'name',
    ),
  })),
);

watch(items, (itemsNew) => {
  if (itemsNew?.length === 1 && combinationTable.value?.combinations?.length === 1) {
    expandedIndices.value = [0];
    combinationTable.value.combinations[0].isEnabled = true;
  }
});

const canGenerate = computed(() => {
  if (props.medicalSystemSetup && instanceOfMvTherapySystemSetupDto(props.medicalSystemSetup)) {
    return (
      unicheckSelected.value &&
      props.medicalSystemSetup &&
      props.medicalSystemSetup?.beamQualityContainer &&
      props.medicalSystemSetup.beamShapingContainer &&
      props.qaEquipmentSetups
    );
  } else {
    return props.medicalSystemSetup && props.qaEquipmentSetups;
  }
});

const parametersChanged = ref(false);

const unicheckSelected = ref(false);

// Combination selection change tracking
const combinationTableBeforeChange = ref<ParameterCombinationTableDto | undefined>(undefined);

function handleChangedSelection() {
  setAreaCombinationChanged(
    props.area.id,
    !isEqual(
      combinationTableBeforeChange.value?.combinations?.map((c) => c.isEnabled),
      combinationTable.value?.combinations?.map((c) => c.isEnabled),
    ),
  );
}

const hasBeamShapings = computed(
  () =>
    props.medicalSystemSetup &&
    instanceOfMvTherapySystemSetupDto(props.medicalSystemSetup) &&
    props.medicalSystemSetup?.beamShapingContainer.isEnabled &&
    props.medicalSystemSetup?.beamShapingContainer.selectedBeamShapings.length,
);

function getParameterValue(param: ISingleSystemSetupParameter) {
  if (
    instanceOfNumericValueDto(param) ||
    instanceOfTextValueDto(param) ||
    instanceOfFaultyNumericValueDto(param) ||
    instanceOfDiscreteNumericValueDto(param) ||
    instanceOfEnumerationValueDto(param)
  ) {
    return param.value;
  }

  if (instanceOfNumericListDto(param)) {
    return param.values;
  }

  if (instanceOfEnumerationValueListDto(param)) {
    return param.selectedOptions;
  }
}

function hasParameterValue(param: ISingleSystemSetupParameter) {
  const value = getParameterValue(param);

  if (isArray(value)) {
    return value.some(negate(isNil));
  } else if (isObject(value)) {
    return !isEmpty(value);
  } else {
    return !isNil(value);
  }
}

function hasActiveParameterValue(param: ISingleSystemSetupParameter) {
  return !!param.isEnabled && hasParameterValue(param);
}

const hasTargetParameterWithValue = computed(
  () =>
    !!props.medicalSystemSetup?.singleSetupParameters.some(hasActiveParameterValue) ||
    hasBeamQualitiesSelected.value ||
    hasBeamShapings.value,
);

const hasSupportParameterWithValue = computed(() => {
  const params = props.qaEquipmentSetups?.flatMap((setup) => setup.singleSetupParameters);
  return params?.filter(truthy).some(hasActiveParameterValue);
});

const hasAnyParameterValue = computed(() =>
  Boolean(hasTargetParameterWithValue.value || hasSupportParameterWithValue.value),
);

const hasBeamQualitiesSelected = computed(
  () =>
    instanceOfMvTherapySystemSetupDto(props.medicalSystemSetup) &&
    checkIfBeamQualitiesAreSelected(props.medicalSystemSetup),
);

const hasCombinations = computed(() => !!items.value?.length);

const hasCombinationsWithAnyValue = computed(
  () => !!items.value?.flatMap((item) => !isEmpty(item.parameters)).every(Boolean),
);

const areCombinationsGenerated = computed(
  () => hasCombinations.value && !parametersChanged.value && canGenerate,
);

function checkForUnicheck() {
  const singleSetupParams = props.qaEquipmentSetups?.flatMap(
    (setup) => setup.singleSetupParameters || [],
  );

  unicheckSelected.value =
    singleSetupParams?.some((item) => item?.name?.includes('Orientation3d')) || false;
}

watch(
  [() => props.medicalSystemSetup?.singleSetupParameters, () => props.qaEquipmentSetups],
  () => {
    parametersChanged.value = true;

    checkForUnicheck();
  },
  { deep: true, immediate: false },
);

onMounted(async () => {
  if (props.area.combinationSetId) {
    await generateInitial();
  }
  checkForUnicheck();
});

const tooManyCombinationsValidationNotification = ref<NotificationMessage>();
const atLeastOneItemMustBeSelectedValidationNotification = ref<NotificationMessage>();

async function handleUpdateCombinations(withValidation: boolean = true) {
  function updateCombinations(combinationsTable: ParameterCombinationTableDto) {
    expandedIndices.value = [];

    combinationTableBeforeChange.value = cloneDeep(combinationsTable);
    combinationTable.value = combinationsTable;

    parametersChanged.value = false;

    setAreaCombinationChanged(props.area.id, false);
    setCominationSetIdValue(combinationsTable.combinationSetId, true);
  }

  function validationErrorHandler(error: Error) {
    if (isSemanticError(error)) {
      if (instanceOfAllCombinationsDisabledExceptionDetails(error.errorResponse?.details)) {
        atLeastOneItemMustBeSelectedValidationNotification.value = {
          type: 'error',
          message: t('Validation.AtLeastOneItemMustBeSelected'),
          closable: false,
        };

        const combinationsTable = error.errorResponse.details.combinationTable;

        if (combinationsTable) {
          updateCombinations(combinationsTable);
        }
      } else if (instanceOfTooManySetupCombinationsExceptionDetails(error.errorResponse?.details)) {
        const total = error.errorResponse.details.totalCombinationCount;
        const max = error.errorResponse.details.maxCombinationCount;

        tooManyCombinationsValidationNotification.value = {
          type: 'error',
          title: t('TooManySetupCombinationsTitle'),
          message: t('TooManySetupCombinationsMessage', { max, total }),
          closable: false,
        };
      }
    } else {
      return error;
    }
  }

  const { result } = await api.taskItemsApi.getSetupCombinations(
    {
      getCombinationTableDto: {
        taskType: props.taskType,
        medicalSystemSetup: props.medicalSystemSetup
          ? toCreateOrUpdateMedicalSystemSetupBaseDto(props.medicalSystemSetup)
          : undefined,

        qaEquipmentSetups: props.qaEquipmentSetups
          ? props.qaEquipmentSetups.map((item) => toCreateTaskItemQaEuipmentSetupDto(item))
          : undefined,

        previousTable: combinationTable.value
          ? toCreateParameterCombinationTableDto(combinationTable.value)
          : undefined,
      },
    },
    [validationErrorHandler, ignoreValidationErrorHandler],
  );

  function resetNotifications() {
    tooManyCombinationsValidationNotification.value = undefined;
    atLeastOneItemMustBeSelectedValidationNotification.value = undefined;
  }

  if (result) {
    resetNotifications();
    updateCombinations(result);
  } else {
    // Validate if combinationsTable was not generated to make user ware of potential invalid inputs
    if (withValidation) validate();
  }
}

async function getSetupCombinations() {
  if (!props.area.combinationSetId || props.area.combinationSetId === NIL) return;

  const { result } = await api.taskItemsApi.getCachedSetupCombinations({
    combinationSetId: props.area.combinationSetId ?? NIL,
  });

  if (result) {
    combinationTable.value = result;
    combinationTableBeforeChange.value = cloneDeep(result);
  }
}

// triggered by the button
const { action: handleUpdateCombinationsAction, loading } = useLoading(handleUpdateCombinations);
const { action: generateInitial } = useLoading(getSetupCombinations, true);

const isReferenceMeasurementAllowed = computed(
  () => mode.value !== 'details' && !!combinationTable.value && !hasCombinationSetIdError.value,
);

const showMeasurementDialog = ref<boolean>(false);

const measurementsForDialog = ref<Measurement | undefined>(undefined);

watch(showMeasurementDialog, (showMeasurementDialogNew) => {
  if (showMeasurementDialogNew === false) {
    measurementsForDialog.value = undefined;
  }
});

const measurementResults = ref<Record<string, TaskItemReferenceMeasurementResultsDto | undefined>>(
  {},
);

function getResult(measurementId?: string) {
  return measurementId ? (measurementResults.value[measurementId] ?? undefined) : undefined;
}

async function addMeasurementResults(measurementId: string) {
  const { result } = await api.taskItemsApi.getTaskItemReferenceMeasurementResults({
    measurementId,
  });

  measurementResults.value[measurementId] = result;
}

const expandedIndices = ref<number[] | undefined>(undefined);

watchEffect(async () => {
  const visibleMeasurementIds = expandedIndices.value
    ?.map(
      (index) =>
        combinationTable.value?.combinations?.[index].referenceMeasurementInfo?.measurementId,
    )
    .filter(truthy);

  for (const measurementId of visibleMeasurementIds ?? []) {
    await addMeasurementResults(measurementId);
  }
});

const { getTitle } = useTaskStepTitle(
  computed<ParameterCombinationDto[]>(() => combinationTable.value?.combinations || []),
);

const { getNotification } = useReferenceMeasurementInfo(t);

const { openReferenceMeasurement, referenceMeasurement, isReferenceMeasurementDialogOpen } =
  useReferenceMeasurement();

const disabled = computed(() => {
  // disable in details mode or when parameters have changed
  if (mode.value === 'details' || hasCombinationSetIdError.value) {
    return true;
  }

  // backend validation via possibleActions
  if (props.taskType === 'BeamConstancyCheck' && combinationTable.value?.possibleActions) {
    const executeAction = combinationTable.value.possibleActions.find(
      (action) => action.action === 'Execute',
    );
    return executeAction?.disabled ?? true;
  }

  return false;
});

const reasonI18nKey = computed(() => {
  const info = combinationTable.value
    ? getActionNotPossibleReason(combinationTable.value, 'Execute')
    : undefined;
  return info?.key ? (info.key as I18nKey) : undefined;
});

// Auto update combination table when no parameters are available
watch(hasAnyParameterValue, async (newValue, oldValue) => {
  if (oldValue && !newValue) {
    await handleUpdateCombinations(false);
  }
});

// Auto update combination table when only the selection of combinations changed
watch(
  () => isCombinationSelectionChangedForArea(props.area.id),
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      pDebounce(async () => handleUpdateCombinations(false), 1000)();
    }
  },
);

const initialCombinationsRequired = computed(() => {
  const hasNoCombinations = !props.area?.combinationSetId || props.area?.combinationSetId === NIL;

  switch (props.taskType) {
    case 'BeamConstancyCheck':
      return hasNoCombinations && hasBeamQualitiesSelected.value && unicheckSelected.value;
    case 'ManualTask':
    default:
      return hasNoCombinations && !!props.medicalSystemSetup;
  }
});

watchEffect(async () => {
  if (initialCombinationsRequired.value) {
    await handleUpdateCombinations(false);
  }
});
</script>

<template>
  <div>
    <!-- Combinations changed and need update notification -->
    <SetupCombinationsUpdateNotification
      :name="`${name}.combinationSetId`"
      @update="handleUpdateCombinationsAction"
    />

    <Notification
      v-if="mode !== 'details' && tooManyCombinationsValidationNotification"
      :value="tooManyCombinationsValidationNotification"
      class="mb-4 pa-4"
    />

    <Notification
      v-if="mode !== 'details' && atLeastOneItemMustBeSelectedValidationNotification"
      :value="atLeastOneItemMustBeSelectedValidationNotification"
      class="mb-4 pa-4"
    />

    <p
      v-if="!hasCombinationsWithAnyValue"
      class="text-body-1 text-primary-lighten-2 font-weight-semibold"
    >
      {{ $t('NoSetupParameterCombinationsMsg') }}
    </p>

    <template v-else>
      <p v-if="hasCombinations && canGenerate">
        {{ $t('SetupParameterCombinationsMsg') }}
      </p>

      <p v-if="areCombinationsGenerated && mode !== 'details'" class="mt-3">
        {{ $t('SetupParameterCombinationsGeneratedMsg') }}
      </p>

      <Loader v-if="loading" type="content" />

      <v-expansion-panels
        v-else-if="hasCombinationsWithAnyValue"
        v-model="expandedIndices"
        class="mt-6"
        multiple
      >
        <v-expansion-panel
          v-for="({ combination }, index) in items"
          :key="`${combination.combinationSetId}-${index}`"
        >
          <v-expansion-panel-title v-if="(items?.length ?? 0) > 1">
            <div class="d-flex w-100 align-center">
              <Checkbox
                v-if="
                  typeof combinationTable?.combinations?.[index].isEnabled === 'boolean' &&
                  combinationTable?.combinations?.length !== 1
                "
                v-model="combinationTable.combinations[index].isEnabled"
                :name="`${combination.combinationSetId}-isEnabled`"
                :disabled="mode === 'details' || hasCombinationSetIdError"
                class="mr-2"
                hideDetails
                @update:modelValue="handleChangedSelection"
                @click.stop
              />

              <TaskStepTitle :combination="getTitle(combination)" />

              <div
                v-if="taskType === 'BeamConstancyCheck'"
                class="ms-auto d-inline-flex align-center"
              >
                <div class="d-flex align-center">
                  <TimeStamp
                    v-if="combination.referenceMeasurementInfo?.creatorUser?.fullName"
                    :time="combination.referenceMeasurementInfo?.creationTime"
                    :user="combination.referenceMeasurementInfo.creatorUser.fullName"
                    type="created"
                    singleLine
                  />
                </div>

                <Notification
                  class="ml-2"
                  :value="
                    getNotification(
                      combination?.referenceMeasurementInfo?.referenceMeasurementState,
                    )
                  "
                />

                <Tooltip v-if="isReferenceMeasurementAllowed" :disabled="!reasonI18nKey">
                  <template #target="{ props: tooltipProps }">
                    <div v-bind="tooltipProps">
                      <Button
                        variant="secondary"
                        :disabled="disabled"
                        :label="$t('ReferenceMeasurement')"
                        size="small"
                        class="ml-2"
                        @click.stop="
                          openReferenceMeasurement(
                            combination,
                            medicalSystem,
                            qaEquipmentSystemEntities,
                          )
                        "
                      />
                    </div>
                  </template>
                  <template v-if="reasonI18nKey" #tooltip>
                    {{ $t(reasonI18nKey) }}
                  </template>
                </Tooltip>
              </div>
            </div>
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <v-row v-if="items?.length === 1">
              <v-col>
                <div class="d-flex w-100 align-center">
                  <Checkbox
                    v-if="typeof combinationTable?.combinations?.[index].isEnabled === 'boolean'"
                    v-model="combinationTable.combinations[index].isEnabled"
                    :disabled="true"
                    :name="`${combination.combinationSetId}-isEnabled`"
                    class="mt-4"
                    hideDetails
                    @update:modelValue="handleChangedSelection"
                    @click.stop
                  />
                  <div
                    v-if="taskType === 'BeamConstancyCheck'"
                    class="ms-auto d-inline-flex align-center"
                  >
                    <div class="d-flex align-center">
                      <TimeStamp
                        v-if="combination.referenceMeasurementInfo?.creatorUser?.fullName"
                        :time="combination.referenceMeasurementInfo?.creationTime"
                        :user="combination.referenceMeasurementInfo.creatorUser.fullName"
                        type="created"
                        singleLine
                      />
                    </div>

                    <Notification
                      class="ml-2"
                      :value="
                        getNotification(
                          combination?.referenceMeasurementInfo?.referenceMeasurementState,
                        )
                      "
                    />

                    <Tooltip v-if="mode !== 'details'" :disabled="!reasonI18nKey">
                      <template #target="{ props: tooltipProps }">
                        <div v-bind="tooltipProps">
                          <Button
                            variant="secondary"
                            :disabled="disabled"
                            :label="$t('ReferenceMeasurement')"
                            class="ml-2"
                            size="small"
                            @click.stop="
                              openReferenceMeasurement(
                                combination,
                                medicalSystem,
                                qaEquipmentSystemEntities,
                              )
                            "
                          />
                        </div>
                      </template>
                      <template v-if="reasonI18nKey" #tooltip>
                        {{ $t(reasonI18nKey) }}
                      </template>
                    </Tooltip>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <ParameterTable
                  :title="$t('TargetSystemParameter_other')"
                  :parameters="combination.targetSystemValues || undefined"
                />
              </v-col>

              <v-col>
                <ParameterTable
                  :title="$t('SupportSystemParameter_other')"
                  :parameters="combination?.supportSystemValues || undefined"
                />
              </v-col>
            </v-row>

            <v-divider
              v-if="getResult(combination.referenceMeasurementInfo?.measurementId)"
              class="mt-4 mb-6"
            />

            <ReferenceMeasurementResults
              :result="getResult(combination.referenceMeasurementInfo?.measurementId)"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <ReferenceMeasurementDialog
        v-model:open="isReferenceMeasurementDialogOpen"
        :measurement="referenceMeasurement"
        @accepted="getSetupCombinations()"
      />
    </template>
  </div>
</template>
